// src/pages/ForgotPasswordPage.tsx
import React, { Suspense } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';
import { Link } from 'react-router-dom';

const ForgotPasswordPageContent: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { showNotification } = useNotification();

    const validationSchema = Yup.object({
        email: Yup.string().email(t('validation.emailInvalid')).required(t('validation.required')),
    });

    return (
        <div className="flex items-center justify-center py-12">
            <div className="max-w-md w-full space-y-8 bg-gray-800 p-10 rounded-xl shadow-lg">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
                        {t('forgotPassword.title')}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-400">
                        {t('forgotPassword.instruction')}
                    </p>
                </div>
                <Formik
                    initialValues={{ email: '' }}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        try {
                            const config = { headers: { 'Accept-Language': i18n.language } };
                            await api.post('/Auth/forgot-password', values, config);
                            showNotification(t('forgotPassword.successMessage'), 'success');
                        } catch (error) {
                            showNotification(t('forgotPassword.errorMessage'), 'error');
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form className="mt-8 space-y-6">
                            <div>
                                <label htmlFor="email" className="sr-only">{t('common.email')}</label>
                                <Field
                                    type="email" name="email" required
                                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholder={t('common.email')}
                                />
                                <ErrorMessage name="email" component="p" className="text-red-400 text-xs italic mt-1" />
                            </div>
                            <div>
                                <button type="submit" disabled={isSubmitting} className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                                    {isSubmitting ? t('common.loading') : t('forgotPassword.submitButton')}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
                <div className="text-sm text-center">
                    <Link to="/login" className="font-medium text-indigo-400 hover:text-indigo-300">
                        {t('forgotPassword.backToLogin')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

const ForgotPasswordPage = () => (
    <Suspense fallback={<div className="text-center p-8 text-white">Ładowanie...</div>}>
        <ForgotPasswordPageContent />
    </Suspense>
);

export default ForgotPasswordPage;